<template>
	<div>
		<v-navigation-drawer
			v-model="taskDrawer"
			temporary
			fixed
			right
			height="100vh"
			app
			:clipped="$vuetify.breakpoint.mdAndUp"
			:width="$vuetify.breakpoint.smAndDown ? '75%' : '35%'"
			:stateless="addTaskDialog"
		>
			<template v-slot:prepend>
				<v-row justify="space-between" class="my-1"> 
					<v-col cols="12" md="5" sm="6" align-self="center">
						<h2 class="mx-2 font-weight-medium"> My Tasks </h2>
					</v-col>
					<v-col cols="12" md="5" sm="6" align-self="center">
						<v-btn
							color="primary"
							@click="addTaskDialog = true"
							rounded
							:disabled="isLoading"
						> 
							Add Tasks 
						</v-btn>
					</v-col>
				</v-row>
			</template>
      
			<v-divider color="#7777"></v-divider>

			<!-- default filler text if no tasks available -->
			<v-row v-if="!items.length" class="my-2" justify="center">
				<v-col cols="12" align-self="center">
					<h1>
						All tasks completed! 
					</h1>

					<v-icon
						x-large 
						color="green"
					>
						mdi-playlist-check
					</v-icon>
				</v-col>
			</v-row>

			<v-row v-if="items.length">
				<v-col>
					<ViewTasks
						:visible="true"
						:items="items"
						:showAssigneeName="false"
						@replaceTask="replaceTask"
						@deletedTaskFromDialog="deletedTaskFromDialog"
						@changeTaskInDialog="changeTaskInDialog"
					/>
				</v-col>
			</v-row>

			<mugen-scroll :handler="fetchData" :should-handle="!loading" v-if="items.length">
				<div v-if="pagination.next" class="mb-4 mt-1">
					<h3>
						Scroll down for more tasks
					</h3>
				</div>
			</mugen-scroll>

			<template v-slot:append>
				<div class="pa-2">
					<v-btn
						block
						:to="{ name: 'AllTasks' }"
						color="primary"
					>
						View All Tasks
						<v-icon right>
							mdi-chevron-right
						</v-icon>
					</v-btn>
				</div>
			</template>
		</v-navigation-drawer>

		<AddTaskDialog
			:visible="addTaskDialog"
			title="Add Task"
			:type="0"
			@closeAddTaskDialog="closeTaskDialog"
			@fetchNewTasks="fetchFreshTasks"
		/>

		<!-- snackbar -->
		<v-snackbar v-model="snackbar.visible" :color="snackbar.color" multi-line>
			{{ snackbar.message }}
			<template v-slot:action="{ attrs }">
				<v-btn dark text v-bind="attrs" @click="snackbar.visible = false">
					Close
				</v-btn>
			</template>
		</v-snackbar>

	</div>
</template>

<script>
import api from "@api/index"
import { mapActions, mapGetters } from "vuex"
import endpoints from "@api/repository"
import Helper from "@utils/misc"
import moment from "moment"
import ViewTasks from "@components/task/ViewTasks.vue"
import AddTaskDialog from "@components/task/AddTaskDialog.vue"
import MugenScroll from 'vue-mugen-scroll'

export default {
	name: 'TaskManagement',
	
	props: {
		"drawer" : {
			type: Boolean,
			default: false
		}
	},
	
	components: {
		ViewTasks,
		AddTaskDialog,
		MugenScroll,
	},

	data() {
		return {
			moment: moment,
			loading:false,
			snackbar: {
				visible: false,
				message: "",
				color: "dark",
			},

			taskDrawer: this.drawer,
			
			items: [],

			undoData: {
				index: null,
				item: null
			},

			pagination: {
				page: 1,
				count: null,
				next: null,
				previous: null,
			},

			addTaskDialog: false,
		}
	},

	computed: {
		...mapGetters([
			"currentUser",
			"accessToken",
			"isLoading",
			"currentInstitution",
		]),

		apiEssentials() {
			return {
				accessToken: this.accessToken,
				setLoading: this.setLoading,
				handleErrorsFunction: this.handleApiErrors,
			};
		},
	},

	methods: {
		...mapActions(["setLoading"]),

		// handle api errors
		async handleApiErrors(err) {
			this.setLoading(false);
			console.log(err);
			if (err.response) {
				this.snackbar.message = "Unable to process request";
				this.snackbar.color = "error";
				this.snackbar.visible = true;
			}
		},

		// trigger function for mugen scroll to fetch tasks from next page
		async fetchData() {
			this.loading = true
			
			if(this.pagination.next && !this.isLoading) {
				var url = this.pagination.next
				var response1 = await api.call(this.apiEssentials, url)

				if(response1) {
					this.pagination.next = response1.next
					this.pagination.count = response1.count
					this.pagination.previous = response1.previous

					response1.results.map(task => this.items.push(task))
				}
			}

			this.loading = false
		},

		// fetch task assigned to current user
		async fetchMyTasks() {
			var url = Helper.addUrlParams(endpoints.tasks, [`assignee__in=${this.currentUser.id}`, 'completed=false', 'is_sub_task=false'])

			var response = await api.call(this.apiEssentials, url)

			if(response) {
				this.items = response.results
				this.pagination.next = response.next
				this.pagination.previous = response.previous
				this.pagination.count = response.count
			}
		},

		// emit from components to fetch all tasks again
		async fetchFreshTasks() {
			var url = Helper.addUrlParams(endpoints.tasks, [`assignee__in=${this.currentUser.id}`, 'completed=false', 'is_sub_task=false'])
			var response = await api.call(this.apiEssentials, url)

			if(response) {
				this.items = response.results
				this.pagination.count = response.count
				this.pagination.next = response.next
				this.pagination.previous = response.previous
			}
		},

		replaceTask(newTask){
			var newTaskIndex = this.items.findIndex(t => t.id == newTask.id)
			this.$set(this.items, newTaskIndex, newTask);
		},

		deletedTaskFromDialog(id, is_sub_task, parent){
			if(!is_sub_task){
				var index = this.items.findIndex(t => t.id == id)
				this.items.splice(index, 1)
			}else{
				var parent_index = this.items.findIndex(t => t.id == parent);
				var sub_task_index = this.items[parent_index].sub_tasks.findIndex(t => t.id == id);
				this.items[parent_index].sub_tasks.splice(sub_task_index, 1);
			}
		},

		async changeTaskInDialog(id){
			var url = endpoints.tasks+`${id}/`
			var response = await api.call(this.apiEssentials, url);
			if(response){
				var oldTaskIndex = this.items.findIndex(t => t.id == id);
				this.items[oldTaskIndex] = response;
				this.$set(this.items, oldTaskIndex, response);
			}
		},

		// close task drawer
		closeDrawer(showDrawer) {
			if(!showDrawer) {
				this.taskDrawer = false
				this.$emit('closeTaskDrawer', showDrawer)
			}
		},

		// close add task dialog
		closeTaskDialog(status) {
			this.addTaskDialog = status
		},
	},

	created() {
		this.fetchMyTasks()
	},

	watch: {
		drawer: {
			async handler() {
				this.taskDrawer = this.drawer

				// fetch tasks whenever drawer is opened
				if(this.drawer) {
					this.fetchMyTasks()
				}
				
				this.$emit('closeTaskDrawer', this.taskDrawer)
			}
		},

		taskDrawer: {
			async handler() {
				this.$emit('closeTaskDrawer', this.taskDrawer)
			} 
		}
	}
}
</script>

<style scoped>
</style>